import React, { useState, useEffect, useRef } from 'react'
import { getServerSessionId } from '../../lib/serverSession'
import { UserEditModal } from './UserEditModal'
import { UserFinancialModal } from './UserFinancialModal'
import { FinancialActionsModal } from './FinancialActionsModal'
import { CommunicationModal } from './CommunicationModal'
import { BulkActionsModal } from './BulkActionsModal'
import { AddUserModal } from './AddUserModal'
import { ReferralLinksModal } from './ReferralLinksModal'
import { UserImpersonationModal } from './UserImpersonationModal'
import { useConfirmationDialog } from './ConfirmationDialog'
import { withAdminAuth, logAdminAction } from '../../lib/adminAuth'
import { findFuzzyMatches, getMatchTypeLabel, getMatchTypeColor } from '../../lib/fuzzySearch'

interface SharePurchase {
  id: string
  package_name: string
  shares_purchased: number
  total_amount: number
  commission_used: number
  remaining_payment: number
  payment_method: string
  status: string
  created_at: string
  updated_at: string
}

interface PaymentTransaction {
  id: string
  amount: number
  currency: string
  network: string
  status: string
  created_at: string
  payment_method?: string
}

interface CommissionBalance {
  usdt_balance: number
  share_balance: number
  total_earned_usdt: number
  total_earned_shares: number
  total_withdrawn_usdt: number
}

interface ReferralData {
  id: string
  referral_code: string
  commission_rate: number
  total_commission: number
  status: string
  created_at: string
  referrer?: {
    id: number
    username: string
    full_name: string | null
  }
  referred?: {
    id: number
    username: string
    full_name: string | null
  }
}

interface KYCInformation {
  id: string
  first_name: string
  last_name: string
  full_legal_name: string
  id_type: string
  phone_number: string
  email_address: string
  street_address: string
  city: string
  postal_code: string
  country_code: string
  country_name: string
  data_consent_given: boolean
  privacy_policy_accepted: boolean
  kyc_completed_at: string
  kyc_status: string
  certificate_requested: boolean
  certificate_generated_at: string | null
  certificate_sent_at: string | null
  created_at: string
}

interface User {
  id: number
  username: string
  email: string
  password_hash: string
  full_name: string | null
  phone: string | null
  phone_number?: string | null
  address: string | null
  is_active: boolean
  is_verified: boolean
  account_status?: string | null
  telegram_id: number | null
  telegram_username?: string | null
  country_of_residence: string | null
  role: string
  is_admin: boolean
  created_at: string
  updated_at: string
  telegram_users?: {
    id: string
    telegram_id: number
    username: string
    first_name: string
    last_name: string
  }[]
  // Financial data
  share_purchases?: SharePurchase[]
  active_share_purchases?: SharePurchase[]
  payment_transactions?: PaymentTransaction[]
  approved_payment_transactions?: PaymentTransaction[]
  commission_balances?: CommissionBalance
  purchased_shares?: number
  bonus_shares?: number
  converted_shares?: number
  commission_shares?: number
  // Referral data
  referrals_made?: ReferralData[]
  referred_by?: ReferralData
  // KYC data
  kyc_information?: KYCInformation
  // Calculated fields
  total_shares?: number
  total_invested?: number
  total_commissions?: number
  total_referrals?: number
  approved_payments_count?: number
  // Downline / entire team (read-only)
  direct_downline_count?: number
  parent_id?: number
  team_level?: number
  // Activity indicators
  last_activity?: string
  activity_score?: number
}

interface UserManagerProps {
  currentUser?: any
  adminUser?: any
  permissions?: any
}

const UserManagerComponent: React.FC<UserManagerProps> = ({ adminUser, currentUser }) => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { showConfirmation, ConfirmationDialog } = useConfirmationDialog()

  // Owner gate for the destructive "Delete Account" (PII scrub) action.
  // Mirrors the quarantine/release RPC trio: Johan, JP, Ruvan.
  const currentAdminEmail = (currentUser?.email || adminUser?.email || '').toLowerCase()
  const OWNER_EMAILS = ['johan@beaversden.co.za', 'jp.rademeyer84@gmail.com', 'krimmel1990@gmail.com']
  const isOwner = OWNER_EMAILS.includes(currentAdminEmail)
  // Account is eligible for deletion only when inactive or quarantined.
  const isDeletable = (u: User) => u.is_active === false || u.account_status === 'quarantined'

  // Parse URL for initial search term (e.g., from hash like #users&search=username)
  const getInitialSearchTerm = () => {
    try {
      const hash = window.location.hash
      if (hash.includes('search=')) {
        const searchMatch = hash.match(/search=([^&]+)/)
        if (searchMatch) {
          return decodeURIComponent(searchMatch[1])
        }
      }
    } catch (e) {
      console.error('Error parsing URL for search term:', e)
    }
    return ''
  }

  const [searchTerm, setSearchTerm] = useState(getInitialSearchTerm())
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(getInitialSearchTerm())
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterRole, setFilterRole] = useState<'all' | 'user' | 'admin'>('all')
  const [filterKYC, setFilterKYC] = useState<'all' | 'completed' | 'pending' | 'none'>('all')
  const [filterInvestment, setFilterInvestment] = useState<'all' | 'investors' | 'non-investors'>('all')
  const [filterReferrals, setFilterReferrals] = useState<'all' | 'has-referrals' | 'no-referrals'>('all')
  const [filterActivity, setFilterActivity] = useState<'all' | 'high' | 'medium' | 'low'>('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showFinancialModal, setShowFinancialModal] = useState(false)
  const [showFinancialActionsModal, setShowFinancialActionsModal] = useState(false)
  const [showCommunicationModal, setShowCommunicationModal] = useState(false)
  const [showBulkActionsModal, setShowBulkActionsModal] = useState(false)
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set())
  const [showReferralLinksModal, setShowReferralLinksModal] = useState(false)
  const [showImpersonationModal, setShowImpersonationModal] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [usersPerPage, setUsersPerPage] = useState(25)
  const [totalUsers, setTotalUsers] = useState(0)
  const [platformSummary, setPlatformSummary] = useState<any>(null)
  const [totalPages, setTotalPages] = useState(0)
  const [sortField, setSortField] = useState<'username' | 'created_at' | 'total_shares' | 'total_invested' | 'total_commissions' | 'total_referrals'>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [searchSuggestions, setSearchSuggestions] = useState<{ value: string; score: number; matchType: string }[]>([])
  const [usersQueryKey, setUsersQueryKey] = useState('')

  // Entire connected team under a searched or clicked user. Read-only.
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set())
  const [downlineMap, setDownlineMap] = useState<Map<number, User[]>>(new Map())
  const [loadingDownlines, setLoadingDownlines] = useState<Set<number>>(new Set())
  const [downlineErrors, setDownlineErrors] = useState<Map<number, string>>(new Map())
  const [teamMeta, setTeamMeta] = useState<Map<number, { count: number; depth: number; truncated: boolean }>>(new Map())
  const teamRequestGen = useRef(0)
  const teamInFlight = useRef<Set<number>>(new Set())
  const fullTeamLoaded = useRef<Set<number>>(new Set())

  // Reset downline state whenever the active page or search changes — stale
  // children from a previous view would otherwise leak into the new context.
  useEffect(() => {
    teamRequestGen.current += 1
    teamInFlight.current = new Set()
    fullTeamLoaded.current = new Set()
    setExpandedUsers(new Set())
    setDownlineMap(new Map())
    setLoadingDownlines(new Set())
    setDownlineErrors(new Map())
    setTeamMeta(new Map())
  }, [currentPage, debouncedSearchTerm, sortField, sortDirection, usersPerPage])

  // Debounce search term - wait 800ms after user stops typing before triggering API call
  useEffect(() => {
    // Don't debounce if search is empty (immediate clear)
    if (searchTerm === '') {
      setDebouncedSearchTerm('')
      return
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setCurrentPage(1) // Reset to page 1 when search changes
    }, 800)

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Load users when page, debounced search, sort, or page size changes
  useEffect(() => {
    loadUsers()
  }, [currentPage, debouncedSearchTerm, sortField, sortDirection, usersPerPage])

  const loadUsers = async () => {
    try {
      setLoading(true)
      setError('')

      console.log(`📊 Fetching users from API: page=${currentPage}, pageSize=${usersPerPage}, search="${debouncedSearchTerm}"`)

      // Use the new optimized API endpoint with server-side pagination
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: usersPerPage.toString(),
        search: debouncedSearchTerm,
        sortField: sortField,
        sortDirection: sortDirection
      })

      const sessionId = getServerSessionId()
      const response = await fetch(`/api/admin/users?${params}`, {
        headers: sessionId ? { 'X-Session-Id': sessionId } : {}
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`)
      }

      const data = await response.json()

      console.log(`✅ Loaded ${data.users.length} users (page ${data.pagination.page} of ${data.pagination.totalPages})`)

      setUsers(data.users)
      setUsersQueryKey(`${debouncedSearchTerm}|${currentPage}|${sortField}|${sortDirection}|${usersPerPage}`)
      setTotalUsers(data.pagination.total)
      setPlatformSummary(data.summary || null)
      setTotalPages(data.pagination.totalPages)

      // Process fuzzy suggestions when search returns no results
      if (data.pagination.total === 0 && debouncedSearchTerm && data.suggestionCandidates) {
        const candidateStrings = data.suggestionCandidates.flatMap((c: any) =>
          [c.username, c.email, c.fullName].filter(Boolean)
        )
        const fuzzyResults = findFuzzyMatches(debouncedSearchTerm, candidateStrings, {
          minScore: 0.3,
          maxResults: 5,
          maxEditDistance: 4
        })
        setSearchSuggestions(fuzzyResults)
      } else {
        setSearchSuggestions([])
      }
    } catch (err: any) {
      console.error('Error loading users:', err)
      setError(err.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  // Client-side filtering for additional filters (KYC, investment, referrals, activity)
  // Search is now handled server-side
  const filteredUsers = users.filter(user => {
    const matchesActiveFilter =
      filterActive === 'all' ||
      (filterActive === 'active' && user.is_active) ||
      (filterActive === 'inactive' && !user.is_active)

    const matchesRoleFilter =
      filterRole === 'all' ||
      (filterRole === 'admin' && user.is_admin) ||
      (filterRole === 'user' && !user.is_admin)

    const matchesKYCFilter =
      filterKYC === 'all' ||
      (filterKYC === 'completed' && user.kyc_information?.kyc_status === 'completed') ||
      (filterKYC === 'pending' && user.kyc_information?.kyc_status === 'pending') ||
      (filterKYC === 'none' && !user.kyc_information)

    const matchesInvestmentFilter =
      filterInvestment === 'all' ||
      (filterInvestment === 'investors' && (user.total_invested || 0) > 0) ||
      (filterInvestment === 'non-investors' && (user.total_invested || 0) === 0)

    const matchesReferralFilter =
      filterReferrals === 'all' ||
      (filterReferrals === 'has-referrals' && (user.total_referrals || 0) > 0) ||
      (filterReferrals === 'no-referrals' && (user.total_referrals || 0) === 0)

    const matchesActivityFilter =
      filterActivity === 'all' ||
      (filterActivity === 'high' && (user.activity_score || 0) >= 70) ||
      (filterActivity === 'medium' && (user.activity_score || 0) >= 30 && (user.activity_score || 0) < 70) ||
      (filterActivity === 'low' && (user.activity_score || 0) < 30)

    // Search is handled server-side, so we only check client-side filters here
    return matchesActiveFilter && matchesRoleFilter && matchesKYCFilter && matchesInvestmentFilter && matchesReferralFilter && matchesActivityFilter
  }).sort((a, b) => {
    let aValue: any = a[sortField]
    let bValue: any = b[sortField]

    // Handle null/undefined values
    if (aValue == null) aValue = 0
    if (bValue == null) bValue = 0

    // Handle string vs number comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()
    }

    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
    } else {
      return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
    }
  })

  // No client-side pagination needed - server handles it
  // Just use the filtered users directly
  const currentUsers = filteredUsers

  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const handleViewFinancials = (user: User) => {
    setSelectedUser(user)
    setShowFinancialModal(true)
  }

  const handleFinancialActions = (user: User) => {
    setSelectedUser(user)
    setShowFinancialActionsModal(true)
  }

  const handleCommunication = (user: User) => {
    setSelectedUser(user)
    setShowCommunicationModal(true)
  }

  const handleViewReferralLinks = (user: User) => {
    setSelectedUser(user)
    setShowReferralLinksModal(true)
  }

  const handleLoginAsUser = async (user: User) => {
    setSelectedUser(user)
    setShowImpersonationModal(true)
  }

  // Names already returned with the user row. Shown at once so the count is not a dead number.
  const directReferralsOf = (user: User): User[] => {
    const seen = new Set<number>()
    const rows: User[] = []
    for (const ref of user.referrals_made || []) {
      const id = Number(ref.referred?.id ?? (ref as { referred_id?: number }).referred_id)
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) continue
      seen.add(id)
      rows.push({
        id,
        username: ref.referred?.username || `user${id}`,
        email: '',
        full_name: ref.referred?.full_name || null,
        password_hash: '',
        phone: null,
        address: null,
        is_active: true,
        is_verified: false,
        telegram_id: null,
        country_of_residence: null,
        role: 'user',
        is_admin: false,
        created_at: ref.created_at || user.created_at,
        updated_at: ref.created_at || user.created_at,
        total_referrals: 0,
        direct_downline_count: 0,
        parent_id: user.id,
        team_level: 1
      })
    }
    return rows
  }

  const showDirectReferrals = (user: User) => {
    const direct = directReferralsOf(user)
    setDownlineMap(prev => {
      if (prev.has(user.id)) return prev
      const next = new Map(prev)
      next.set(user.id, direct)
      return next
    })
    setExpandedUsers(prev => {
      const next = new Set(prev)
      next.add(user.id)
      return next
    })
    setTeamMeta(prev => {
      if (prev.has(user.id)) return prev
      const next = new Map(prev)
      next.set(user.id, { count: direct.length, depth: direct.length > 0 ? 1 : 0, truncated: false })
      return next
    })
  }

  // Load every person connected under userId (all levels). Read-only.
  // A later search or page change bumps teamRequestGen and drops the result.
  const loadEntireTeam = async (userId: number) => {
    if (teamInFlight.current.has(userId)) return
    const gen = teamRequestGen.current
    teamInFlight.current.add(userId)

    setExpandedUsers(prev => {
      const next = new Set(prev)
      next.add(userId)
      return next
    })
    setLoadingDownlines(prev => {
      const next = new Set(prev)
      next.add(userId)
      return next
    })
    setDownlineErrors(prev => {
      const next = new Map(prev)
      next.delete(userId)
      return next
    })

    try {
      const sessionId = getServerSessionId()
      const response = await fetch(`/api/admin/user-downline?userId=${userId}&scope=team`, {
        headers: sessionId ? { 'X-Session-Id': sessionId } : {}
      })
      if (!response.ok) throw new Error(`API error: ${response.statusText}`)
      const data = await response.json()
      if (teamRequestGen.current !== gen) return

      const members: User[] = Array.isArray(data.downline) ? data.downline : []
      const grouped = new Map<number, User[]>()
      for (const member of members) {
        const parentId = Number(member.parent_id)
        if (!Number.isFinite(parentId)) continue
        const list = grouped.get(parentId) || []
        list.push(member)
        grouped.set(parentId, list)
      }
      if (!grouped.has(userId)) grouped.set(userId, [])

      setDownlineMap(prev => {
        const next = new Map(prev)
        for (const [parentId, list] of grouped) next.set(parentId, list)
        return next
      })
      setExpandedUsers(prev => {
        const next = new Set(prev)
        next.add(userId)
        for (const parentId of grouped.keys()) {
          if ((grouped.get(parentId) || []).length > 0) next.add(parentId)
        }
        return next
      })
      setTeamMeta(prev => {
        const next = new Map(prev)
        next.set(userId, {
          count: Number(data.count) || members.length,
          depth: Number(data.depth) || 0,
          truncated: Boolean(data.truncated)
        })
        return next
      })
      fullTeamLoaded.current.add(userId)
    } catch (err: any) {
      if (teamRequestGen.current !== gen) return
      console.error('Failed to load team for user', userId, err)
      setDownlineErrors(prev => {
        const next = new Map(prev)
        next.set(userId, err?.message || 'Failed to load team')
        return next
      })
    } finally {
      teamInFlight.current.delete(userId)
      if (teamRequestGen.current === gen) {
        setLoadingDownlines(prev => {
          const next = new Set(prev)
          next.delete(userId)
          return next
        })
      }
    }
  }

  const openReferrals = (user: User) => {
    if (loadingDownlines.has(user.id)) return
    if (expandedUsers.has(user.id)) {
      setExpandedUsers(prev => {
        const next = new Set(prev)
        next.delete(user.id)
        return next
      })
      return
    }
    showDirectReferrals(user)
    if (!fullTeamLoaded.current.has(user.id)) void loadEntireTeam(user.id)
  }

  const toggleDownline = async (userId: number) => {
    if (loadingDownlines.has(userId)) return
    if (expandedUsers.has(userId)) {
      setExpandedUsers(prev => {
        const next = new Set(prev)
        next.delete(userId)
        return next
      })
      return
    }

    const onPage = users.find(user => user.id === userId)
    if (onPage) {
      openReferrals(onPage)
      return
    }

    if (downlineMap.has(userId)) {
      setExpandedUsers(prev => {
        const next = new Set(prev)
        next.add(userId)
        return next
      })
      return
    }

    await loadEntireTeam(userId)
  }

  // A username search already includes each match's direct referrals.
  // Open those names under every match, then load the rest of the team.
  useEffect(() => {
    const queryKey = `${debouncedSearchTerm}|${currentPage}|${sortField}|${sortDirection}|${usersPerPage}`
    if (!debouncedSearchTerm.trim()) return
    if (usersQueryKey !== queryKey) return

    const withReferrals = users.filter(user => (user.referrals_made?.length || 0) > 0 || (user.total_referrals || 0) > 0)
    if (withReferrals.length === 0) return

    for (const user of withReferrals) showDirectReferrals(user)
    for (const user of withReferrals.slice(0, 4)) {
      if (!fullTeamLoaded.current.has(user.id)) void loadEntireTeam(user.id)
    }
  }, [users, usersQueryKey, debouncedSearchTerm, currentPage, sortField, sortDirection, usersPerPage])

  const handleUserSelection = (userId: number, selected: boolean) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev)
      if (selected) {
        newSet.add(userId)
      } else {
        newSet.delete(userId)
      }
      return newSet
    })
  }

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)))
    } else {
      setSelectedUserIds(new Set())
    }
  }

  const handleBulkActions = () => {
    if (selectedUserIds.size === 0) {
      alert('Please select users first')
      return
    }
    setShowBulkActionsModal(true)
  }

  const clearSelection = () => {
    setSelectedUserIds(new Set())
  }

  const selectedUsers = users.filter(u => selectedUserIds.has(u.id))

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return '↕️'
    return sortDirection === 'asc' ? '↑' : '↓'
  }

  const handleDeleteUser = async (userId: number) => {
    const user = users.find(u => u.id === userId)
    if (!user) return

    showConfirmation({
      title: '🔒 Quarantine User Account',
      message: `Are you sure you want to quarantine user "${user.username}"?\n\nThis will:\n• LOCK the account completely (no login, no transactions)\n• PRESERVE all financial data, commissions, referrals, shares, and ledger history\n• Require Ruvan, Johan, or JP to release the account\n\nAll data remains untouched and secure until further investigation.`,
      type: 'danger',
      confirmText: 'Quarantine Account',
      onConfirm: async () => {
        try {
          console.log('🔒 Starting account quarantine for user ID:', userId)

          const sessionId = getServerSessionId()
          const response = await fetch('/api/admin/user-management', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(sessionId ? { 'X-Session-Id': sessionId } : {})
            },
            body: JSON.stringify({
              operation: 'delete_user',
              data: { userId, reason: 'Admin quarantine via dashboard - account locked pending investigation' }
            })
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.error || errorData.message || 'Failed to quarantine user')
          }

          const result = await response.json()
          const details = result.data?.quarantineDetails || {}

          // Log admin action
          try {
            await logAdminAction(
              adminUser?.email || 'unknown',
              'QUARANTINE_USER',
              'user',
              userId.toString(),
              {
                reason: 'Admin quarantine - account locked pending investigation',
                username: user.username,
                quarantineDetails: details
              }
            )
          } catch (auditError) {
            console.warn('Failed to log admin action (user still quarantined):', auditError)
          }

          await loadUsers()

          let summaryMessage = `🔒 User "${user.username}" has been QUARANTINED successfully!\n\n`
          summaryMessage += `📊 QUARANTINE SUMMARY:\n`
          summaryMessage += `• Account Status: LOCKED\n`
          summaryMessage += `• Financial Data: PRESERVED & UNTOUCHED\n`
          summaryMessage += `• Commissions: PRESERVED & UNTOUCHED\n`
          summaryMessage += `• Referral Tree: PRESERVED & UNTOUCHED\n`
          summaryMessage += `• Shares & Certificates: PRESERVED & UNTOUCHED\n\n`
          summaryMessage += `🔐 Account is completely locked out.\n`
          summaryMessage += `Only Ruvan, Johan, or JP can release this account.\n`
          summaryMessage += `All data secured for investigation.`

          alert(summaryMessage)
        } catch (error: any) {
          console.error('Error quarantining user:', error)
          alert('Failed to quarantine user: ' + error.message)
        }
      }
    })
  }

  // OWNER-ONLY "Delete Account": removes personal data (email, username, phone,
  // ID number, name, address) so the original email can be reused for a fresh
  // registration. ALL financial records are preserved server-side.
  const handleScrubDeleteUser = async (userId: number) => {
    const user = users.find(u => u.id === userId)
    if (!user) return
    if (!isOwner) {
      alert('Only Johan, JP, or Ruvan can delete accounts.')
      return
    }
    if (!isDeletable(user)) {
      alert('Only inactive or quarantined accounts can be deleted.')
      return
    }

    showConfirmation({
      title: '🗑️ Delete Account (remove personal data)',
      message: `Permanently DELETE the personal data for "${user.username}"?\n\nThis will:\n• REMOVE email, username, phone, ID number, name & address\n• FREE the original email address for re-registration\n• PRESERVE all financial records (shares, commissions, withdrawals, payments, referrals)\n\nThis action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete Account',
      onConfirm: async () => {
        try {
          const sessionId = getServerSessionId()
          const response = await fetch('/api/admin/user-management', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(sessionId ? { 'X-Session-Id': sessionId } : {})
            },
            body: JSON.stringify({
              operation: 'scrub_delete_user',
              data: { userId }
            })
          })

          if (!response.ok) {
            const errorData = await response.json()
            throw new Error(errorData.message || errorData.error || 'Failed to delete account')
          }

          try {
            await logAdminAction(
              currentUser?.email || adminUser?.email || 'unknown',
              'SCRUB_DELETE_USER',
              'user',
              userId.toString(),
              { username: user.username, reason: 'Owner deletion - PII scrubbed, financial data preserved' }
            )
          } catch (auditError) {
            console.warn('Failed to log admin action (account still deleted):', auditError)
          }

          await loadUsers()
          alert(`🗑️ Account "${user.username}" deleted.\n\nPersonal data removed and the original email is now free for re-registration.\nAll financial records were preserved.`)
        } catch (error: any) {
          console.error('Error deleting account:', error)
          alert('Failed to delete account: ' + error.message)
        }
      }
    })
  }

  const toggleUserStatus = async (userId: number, currentStatus: boolean) => {
    try {
      const sessionId = getServerSessionId()
      const response = await fetch('/api/admin/user-management', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'toggle_user_status',
          data: { userId, currentStatus }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to update user status')
      }

      // Log admin action
      await logAdminAction(
        adminUser?.email || 'unknown',
        currentStatus ? 'DEACTIVATE_USER' : 'ACTIVATE_USER',
        'user',
        userId.toString(),
        { previous_status: currentStatus, new_status: !currentStatus }
      )

      await loadUsers()
    } catch (err: any) {
      console.error('Error updating user status:', err)
      alert('Failed to update user status: ' + err.message)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
        <span className="ml-3 text-gray-300">Loading users...</span>
      </div>
    )
  }

  // Calculate summary statistics for CURRENT PAGE only
  // Note: These are now page-level stats, not system-wide
  const activeUsersOnPage = users.filter(u => u.is_active).length

  // Share breakdown for current page
  const totalPurchasedShares = users.reduce((sum, user) => sum + (user.purchased_shares || 0), 0)
  const totalBonusShares = users.reduce((sum, user) => sum + (user.bonus_shares || 0), 0)
  const totalConvertedShares = users.reduce((sum, user) => sum + (user.converted_shares || 0), 0)
  const totalCommissionShares = users.reduce((sum, user) => sum + (user.commission_shares || 0), 0)
  const totalSharesAllUsers = users.reduce((sum, user) => sum + (user.total_shares || 0), 0)

  const totalInvestedAllUsers = users.reduce((sum, user) => sum + (user.total_invested || 0), 0)
  const totalCommissionsAllUsers = users.reduce((sum, user) => sum + (user.total_commissions || 0), 0)
  const totalReferralsAllUsers = users.reduce((sum, user) => sum + (user.total_referrals || 0), 0)
  const usersWithReferrals = users.filter(u => (u.total_referrals || 0) > 0).length
  const usersWithKYC = users.filter(u => u.kyc_information).length
  const kycCompleted = users.filter(u => u.kyc_information?.kyc_status === 'completed').length
  const pageOnlyStats = !platformSummary
  const cardTotalUsers = pageOnlyStats ? totalUsers : platformSummary.totalUsers
  const cardActiveUsers = pageOnlyStats ? activeUsersOnPage : platformSummary.activeUsers
  const cardPurchasedShares = pageOnlyStats ? totalPurchasedShares : platformSummary.purchasedShares
  const cardBonusShares = pageOnlyStats ? totalBonusShares : platformSummary.bonusShares
  const cardConvertedShares = pageOnlyStats ? totalConvertedShares : platformSummary.convertedShares
  const cardCommissionShares = pageOnlyStats ? totalCommissionShares : platformSummary.commissionShares
  const cardTotalShares = pageOnlyStats ? totalSharesAllUsers : platformSummary.totalShares
  const cardTotalInvested = pageOnlyStats ? totalInvestedAllUsers : platformSummary.totalInvested
  const cardTotalCommissions = pageOnlyStats ? totalCommissionsAllUsers : platformSummary.totalCommissions
  const cardTotalReferrals = pageOnlyStats ? totalReferralsAllUsers : platformSummary.totalReferrals
  const cardActiveReferrers = pageOnlyStats ? usersWithReferrals : platformSummary.activeReferrers
  const cardKycCompleted = pageOnlyStats ? kycCompleted : platformSummary.kycCompleted
  const cardKycTotal = pageOnlyStats ? usersWithKYC : platformSummary.kycTotal

  // Entire connected team. Render depth is a safety stop for a broken cycle,
  // not a business limit. The API already refuses cycles.
  const MAX_TEAM_RENDER_DEPTH = 50
  const renderDownlineRows = (parentId: number, depth: number): React.ReactNode => {
    if (!expandedUsers.has(parentId)) return null
    if (depth > MAX_TEAM_RENDER_DEPTH) return null

    const isLoading = loadingDownlines.has(parentId)
    const errorMsg = downlineErrors.get(parentId)
    const children = downlineMap.get(parentId)
    const meta = depth === 1 ? teamMeta.get(parentId) : undefined

    const depthAccents = [
      'border-l-cyan-500/60 bg-cyan-500/5',
      'border-l-purple-500/60 bg-purple-500/5',
      'border-l-pink-500/60 bg-pink-500/5',
      'border-l-amber-500/60 bg-amber-500/5',
      'border-l-emerald-500/60 bg-emerald-500/5'
    ]
    const depthBadges = [
      'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
      'bg-purple-500/20 text-purple-300 border-purple-500/40',
      'bg-pink-500/20 text-pink-300 border-pink-500/40',
      'bg-amber-500/20 text-amber-300 border-amber-500/40',
      'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
    ]
    const depthAccent = depthAccents[(depth - 1) % depthAccents.length]
    const depthBadgeColor = depthBadges[(depth - 1) % depthBadges.length]
    const indentPx = Math.min(depth, 8) * 20

    if (isLoading && (!children || children.length === 0)) {
      return (
        <tr key={`dl-loading-${parentId}-${depth}`} className={`border-l-4 ${depthAccent}`}>
          <td colSpan={11} className="px-4 py-3">
            <div className="flex items-center text-sm text-gray-300" style={{ paddingLeft: indentPx }}>
              <span className={`px-2 py-0.5 text-xs font-bold rounded-md border mr-3 ${depthBadgeColor}`}>L{depth}</span>
              <span className="inline-block w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin mr-2"></span>
              Loading entire team…
            </div>
          </td>
        </tr>
      )
    }

    if (errorMsg && (!children || children.length === 0)) {
      return (
        <tr key={`dl-error-${parentId}-${depth}`} className={`border-l-4 ${depthAccent}`}>
          <td colSpan={11} className="px-4 py-3">
            <div className="flex items-center text-sm text-red-400" style={{ paddingLeft: indentPx }}>
              <span className={`px-2 py-0.5 text-xs font-bold rounded-md border mr-3 ${depthBadgeColor}`}>L{depth}</span>
              ⚠️ {errorMsg}
            </div>
          </td>
        </tr>
      )
    }

    if (!children || children.length === 0) {
      return (
        <tr key={`dl-empty-${parentId}-${depth}`} className={`border-l-4 ${depthAccent}`}>
          <td colSpan={11} className="px-4 py-3">
            <div className="flex items-center text-sm text-gray-500 italic" style={{ paddingLeft: indentPx }}>
              <span className={`px-2 py-0.5 text-xs font-bold rounded-md border mr-3 ${depthBadgeColor}`}>L{depth}</span>
              No direct downline.
            </div>
          </td>
        </tr>
      )
    }

    const teamBanner = meta ? (
      <tr key={`dl-team-${parentId}`} className={`border-l-4 ${depthAccent}`}>
        <td colSpan={11} className="px-4 py-2">
          <div className="text-sm text-purple-200" style={{ paddingLeft: indentPx }}>
            {meta.depth <= 1 ? 'Direct referrals' : 'Entire team'}: {meta.count.toLocaleString()} {meta.count === 1 ? 'person' : 'people'}
            {meta.depth > 1 ? ` across ${meta.depth} levels` : ''}
            {isLoading ? ' · loading the rest of the team' : ''}
            {meta.truncated ? ' · this list stopped at the safety limit' : ''}
          </div>
        </td>
      </tr>
    ) : null

    return (
      <>
        {teamBanner}
        {children.map((child) => {
          const childIsExpanded = expandedUsers.has(child.id)
          const loadedChildren = downlineMap.get(child.id)
          const childHasDownline = (loadedChildren && loadedChildren.length > 0) || (child.direct_downline_count || 0) > 0
          const canExpandFurther = childHasDownline && depth < MAX_TEAM_RENDER_DEPTH
          return (
            <React.Fragment key={`dl-${parentId}-${child.id}-${depth}`}>
              <tr className={`border-l-4 ${depthAccent} hover:bg-gray-800/40 transition-colors`}>
                <td colSpan={11} className="px-4 py-3">
                  <div className="flex items-center flex-wrap gap-3" style={{ paddingLeft: indentPx }}>
                    <span className={`px-2 py-0.5 text-xs font-bold rounded-md border ${depthBadgeColor}`}>
                      L{depth}
                    </span>
                    {canExpandFurther ? (
                      <button
                        onClick={() => toggleDownline(child.id)}
                        title={childIsExpanded ? 'Hide this team' : 'Show the entire team connected to this user'}
                        className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-700/60 hover:bg-yellow-500/30 border border-gray-600 hover:border-yellow-500/60 text-yellow-400 transition-colors"
                      >
                        {loadingDownlines.has(child.id)
                          ? <span className="inline-block w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></span>
                          : childIsExpanded ? '▼' : '▶'}
                      </button>
                    ) : (
                      <span className="w-6 h-6 inline-block opacity-30 text-gray-500 text-center">·</span>
                    )}
                    <div className="flex-1 min-w-[180px]">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-white truncate max-w-[180px]">
                          {child.full_name || child.username}
                        </span>
                        {child.is_admin && (
                          <span className="px-1.5 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">👑</span>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${child.is_active ? 'bg-green-500/15 text-green-300 border-green-500/30' : 'bg-red-500/15 text-red-300 border-red-500/30'}`}>
                          {child.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">@{child.username} · ID:{child.id} · {child.email}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-1 text-xs rounded-md bg-yellow-500/10 text-yellow-300 border border-yellow-500/30">📈 {(child.total_shares || 0).toLocaleString()} shares</span>
                      <span className="px-2 py-1 text-xs rounded-md bg-green-500/10 text-green-300 border border-green-500/30">💰 ${(child.total_invested || 0).toLocaleString()}</span>
                      <span className="px-2 py-1 text-xs rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/30">🤝 ${(child.total_commissions || 0).toLocaleString()}</span>
                      {(child.total_referrals || 0) > 0 ? (
                        <button
                          type="button"
                          onClick={() => toggleDownline(child.id)}
                          title="Show the entire team connected to this user"
                          className="px-2 py-1 text-xs rounded-md bg-purple-500/10 text-purple-300 border border-purple-500/30 hover:bg-purple-500/20"
                        >
                          👥 {(child.total_referrals || 0).toLocaleString()} refs
                        </button>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-md bg-purple-500/10 text-purple-300 border border-purple-500/30">👥 0 refs</span>
                      )}
                      {child.kyc_information ? (
                        <span className={`px-2 py-1 text-xs rounded-md border ${child.kyc_information.kyc_status === 'completed' ? 'bg-green-500/10 text-green-300 border-green-500/30' : child.kyc_information.kyc_status === 'pending' ? 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30' : 'bg-red-500/10 text-red-300 border-red-500/30'}`}>
                          🆔 {child.kyc_information.kyc_status}
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-md bg-gray-700/40 text-gray-400 border border-gray-600/40">🆔 None</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleViewFinancials(child)}
                        className="px-2 py-1 text-xs rounded-md bg-yellow-500/15 text-yellow-300 border border-yellow-500/30 hover:bg-yellow-500/25 transition-colors"
                      >
                        💰 Financials
                      </button>
                      <button
                        onClick={() => handleEditUser(child)}
                        className="px-2 py-1 text-xs rounded-md bg-blue-500/15 text-blue-300 border border-blue-500/30 hover:bg-blue-500/25 transition-colors"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleLoginAsUser(child)}
                        className="px-2 py-1 text-xs rounded-md bg-orange-500/15 text-orange-300 border border-orange-500/30 hover:bg-orange-500/25 transition-colors"
                      >
                        👤 Login
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
              {childIsExpanded && renderDownlineRows(child.id, depth + 1)}
            </React.Fragment>
          )
        })}
      </>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-red-500">🔴 UPDATED USER MANAGEMENT 🔴</h2>
          <p className="text-white mt-1">
            Manage user accounts, permissions, and profile information
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-white">
            Total Users: {totalUsers.toLocaleString()} | On page: {users.length}
          </div>
          <button
            onClick={() => setShowAddUserModal(true)}
            className="px-4 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 transition-colors flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Performance Notice */}
      <div className="glass-card p-4 border border-blue-500/30 bg-blue-500/5">
        <div className="flex items-start space-x-3">
          <div className="text-2xl">⚡</div>
          <div>
            <p className="text-sm font-medium text-blue-400">Optimized Performance</p>
            <p className="text-sm text-white mt-1">
              Loading {usersPerPage} users per page with server-side pagination. {pageOnlyStats ? 'Summary cards fell back to this page only.' : 'Summary cards are platform totals. The table is this page.'} Total users: {totalUsers.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced Financial Summary Cards (platform totals) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="glass-card p-6 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white uppercase tracking-wider">Total Users</p>
              <p className="text-3xl font-bold text-white mt-2">{cardTotalUsers.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <p className="text-sm text-green-400 font-medium">{cardActiveUsers.toLocaleString()} active · {activeUsersOnPage} on page</p>
              </div>
            </div>
            <div className="text-4xl opacity-80">👥</div>
          </div>
        </div>

        <div className="glass-card p-6 border border-yellow-500/20 hover:border-yellow-500/40 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white uppercase tracking-wider">Total Shares</p>
              <p className="text-3xl font-bold text-yellow-400 mt-2">{cardTotalShares.toLocaleString()}</p>
              <div className="mt-3 space-y-1 text-sm text-white">
                <div>📦 Purchased: {cardPurchasedShares.toLocaleString()}</div>
                <div>🎁 Bonus: {cardBonusShares.toLocaleString()}</div>
                <div>🔄 Converted: {cardConvertedShares.toLocaleString()}</div>
                <div>💸 Commission: {cardCommissionShares.toLocaleString()}</div>
              </div>
            </div>
            <div className="text-4xl opacity-80">📈</div>
          </div>
        </div>

        <div className="glass-card p-6 border border-green-500/20 hover:border-green-500/40 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white uppercase tracking-wider">Amount Paid</p>
              <p className="text-3xl font-bold text-green-400 mt-2">${cardTotalInvested.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                <p className="text-sm text-green-400 font-medium">{pageOnlyStats ? 'This page only' : 'All users combined'}</p>
              </div>
            </div>
            <div className="text-4xl opacity-80">💰</div>
          </div>
        </div>

        <div className="glass-card p-6 border border-blue-500/20 hover:border-blue-500/40 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white uppercase tracking-wider">Total Commissions (USDT)</p>
              <p className="text-3xl font-bold text-blue-400 mt-2">${cardTotalCommissions.toLocaleString()}</p>
              <div className="mt-3 space-y-1 text-sm text-white">
                <div>💰 USDT Earned: ${cardTotalCommissions.toLocaleString()}</div>
                <div>📊 Share Commission: {cardCommissionShares.toLocaleString()} shares</div>
              </div>
            </div>
            <div className="text-4xl opacity-80">🤝</div>
          </div>
        </div>

        <div className="glass-card p-6 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white uppercase tracking-wider">Total Referrals</p>
              <p className="text-3xl font-bold text-purple-400 mt-2">{cardTotalReferrals.toLocaleString()}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full mr-2"></div>
                <p className="text-sm text-purple-400 font-medium">{cardActiveReferrers.toLocaleString()} active referrers</p>
              </div>
            </div>
            <div className="text-4xl opacity-80">👥</div>
          </div>
        </div>

        <div className="glass-card p-6 border border-orange-500/20 hover:border-orange-500/40 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white uppercase tracking-wider">KYC Status</p>
              <p className="text-3xl font-bold text-orange-400 mt-2">{cardKycCompleted}/{cardKycTotal}</p>
              <div className="flex items-center mt-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full mr-2"></div>
                <p className="text-sm text-orange-400 font-medium">Completed/Total</p>
              </div>
            </div>
            <div className="text-4xl opacity-80">🆔</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-red-400">❌ {error}</p>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedUserIds.size > 0 && (
        <div className="glass-card p-4 bg-blue-900/20 border border-blue-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-blue-400 font-medium">
                {selectedUserIds.size} users selected
              </span>
              <button
                onClick={clearSelection}
                className="text-gray-400 hover:text-white text-sm"
              >
                Clear Selection
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleBulkActions}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                🔧 Bulk Actions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="glass-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-4">
          {/* Search */}
          <div className="md:col-span-2 xl:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              🔍 Search Users
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by user ID, username, email, phone, telegram username, name, or sponsor..."
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
            />
          </div>

          {/* Active Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              📊 Status
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            >
              <option value="all">All Users</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Role Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              👑 Role
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            >
              <option value="all">All Roles</option>
              <option value="user">Users Only</option>
              <option value="admin">Admins Only</option>
            </select>
          </div>

          {/* KYC Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              🆔 KYC Status
            </label>
            <select
              value={filterKYC}
              onChange={(e) => setFilterKYC(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            >
              <option value="all">All KYC</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="none">No KYC</option>
            </select>
          </div>

          {/* Share Purchase Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              💰 Share Purchase
            </label>
            <select
              value={filterInvestment}
              onChange={(e) => setFilterInvestment(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            >
              <option value="all">All Users</option>
              <option value="investors">Shareholders</option>
              <option value="non-investors">Non-Shareholders</option>
            </select>
          </div>

          {/* Referrals Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              👥 Referrals
            </label>
            <select
              value={filterReferrals}
              onChange={(e) => setFilterReferrals(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            >
              <option value="all">All Users</option>
              <option value="has-referrals">Has Referrals</option>
              <option value="no-referrals">No Referrals</option>
            </select>
          </div>

          {/* Activity Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              📊 Activity
            </label>
            <select
              value={filterActivity}
              onChange={(e) => setFilterActivity(e.target.value as any)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            >
              <option value="all">All Levels</option>
              <option value="high">High (70%+)</option>
              <option value="medium">Medium (30-70%)</option>
              <option value="low">Low (&lt;30%)</option>
            </select>
          </div>
        </div>
      </div>

      {/* No Results Found State */}
      {!loading && currentUsers.length === 0 && debouncedSearchTerm && (
        <div className="glass-card p-8 text-center">
          <div className="text-5xl mb-4">🔍</div>
          <h3 className="text-xl font-semibold text-white mb-2">No users found</h3>
          <p className="text-gray-400 mb-4">
            No users match "<span className="text-yellow-400 font-medium">{debouncedSearchTerm}</span>" across username, email, name, or phone.
          </p>
          {searchSuggestions.length > 0 && (
            <div className="mt-4 max-w-md mx-auto">
              <p className="text-sm text-gray-400 mb-3">Did you mean:</p>
              <div className="space-y-2">
                {searchSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => setSearchTerm(suggestion.value)}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-gray-800/60 border border-gray-700 hover:border-yellow-500/50 hover:bg-gray-800 transition-all duration-200 group"
                  >
                    <span className="text-white group-hover:text-yellow-400 font-medium transition-colors">
                      {suggestion.value}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getMatchTypeColor(suggestion.matchType as any)}`}>
                      {getMatchTypeLabel(suggestion.matchType as any)}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {searchSuggestions.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Try using fewer characters or a different search term.
            </p>
          )}
          <button
            onClick={() => setSearchTerm('')}
            className="mt-6 px-5 py-2 text-sm font-medium rounded-lg bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600 hover:text-white transition-colors"
          >
            ✕ Clear Search
          </button>
        </div>
      )}

      {/* Users Table - Desktop */}
      <div className={`glass-card overflow-hidden hidden md:block ${!loading && currentUsers.length === 0 && debouncedSearchTerm ? 'md:hidden' : ''}`}>
        <div className="overflow-x-auto bg-gray-900/50 rounded-lg border border-gray-700">
          <table className="w-full min-w-[1400px]">
            <thead className="bg-gray-800/70 border-b border-gray-600">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-medium text-gray-300 uppercase tracking-wider w-16">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500"
                  />
                </th>
                <th
                  className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-64 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('username')}
                >
                  <div className="flex items-center space-x-1">
                    <span>👤 User</span>
                    <span className="text-yellow-400">{getSortIcon('username')}</span>
                  </div>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-28">
                  <div className="flex items-center space-x-1">
                    <span>📊 Status</span>
                  </div>
                </th>
                <th
                  className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('total_shares')}
                >
                  <div className="flex items-center space-x-1">
                    <span>📈 Shares</span>
                    <span className="text-yellow-400">{getSortIcon('total_shares')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('total_invested')}
                >
                  <div className="flex items-center space-x-1">
                    <span>💰 Amount Paid</span>
                    <span className="text-yellow-400">{getSortIcon('total_invested')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('total_commissions')}
                >
                  <div className="flex items-center space-x-1">
                    <span>🤝 Commissions</span>
                    <span className="text-yellow-400">{getSortIcon('total_commissions')}</span>
                  </div>
                </th>
                <th
                  className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('total_referrals')}
                >
                  <div className="flex items-center space-x-1">
                    <span>👥 Referrals</span>
                    <span className="text-yellow-400">{getSortIcon('total_referrals')}</span>
                  </div>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-24">
                  <div className="flex items-center space-x-1">
                    <span>🆔 KYC</span>
                  </div>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-32">
                  <div className="flex items-center space-x-1">
                    <span>🤝 Sponsor</span>
                  </div>
                </th>
                <th
                  className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-700/50 transition-colors"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>📅 Created</span>
                    <span className="text-yellow-400">{getSortIcon('created_at')}</span>
                  </div>
                </th>
                <th className="px-4 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider w-48">
                  <div className="flex items-center space-x-1">
                    <span>⚡ Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50 bg-gray-900/30">
              {currentUsers.map((user) => (
                <React.Fragment key={user.id}>
                <tr className="hover:bg-gray-800/40 transition-all duration-200 border-b border-gray-700/30">
                  <td className="px-4 py-5 w-16">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(user.id)}
                      onChange={(e) => handleUserSelection(user.id, e.target.checked)}
                      className="rounded border-gray-600 bg-gray-800 text-yellow-500 focus:ring-yellow-500 w-4 h-4"
                    />
                  </td>
                  <td className="px-4 py-5 w-64">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        {(user.total_referrals || 0) > 0 ? (
                          <button
                            onClick={() => openReferrals(user)}
                            title={expandedUsers.has(user.id) ? 'Hide referrals' : 'View referrals'}
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-700/60 hover:bg-yellow-500/30 border border-gray-600 hover:border-yellow-500/60 text-yellow-400 transition-colors flex-shrink-0"
                          >
                            {loadingDownlines.has(user.id)
                              ? <span className="inline-block w-3 h-3 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></span>
                              : expandedUsers.has(user.id) ? '▼' : '▶'}
                          </button>
                        ) : (
                          <span className="w-7 h-7 inline-block opacity-20 text-gray-500 text-center flex-shrink-0">·</span>
                        )}
                        <div className="text-base font-semibold text-white truncate max-w-[180px]">
                          {user.full_name || user.username}
                        </div>
                        {user.is_admin && (
                          <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full bg-yellow-500/20 text-yellow-400">
                            👑 Admin
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white">@{user.username}</div>
                      <div className="text-sm text-gray-500">ID: {user.id}</div>
                      <div className="text-sm text-gray-300 truncate max-w-[200px]">{user.email}</div>
                      {user.phone && (
                        <div className="text-sm text-white truncate">{user.phone}</div>
                      )}
                      {user.telegram_users && user.telegram_users.length > 0 ? (
                        <div className="text-sm text-blue-400 truncate max-w-[200px]">
                          📱 @{user.telegram_users[0].username} ({user.telegram_users[0].first_name} {user.telegram_users[0].last_name})
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">📱 No Telegram</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-5 w-28">
                    <div className="flex flex-col space-y-2">
                      <span className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full w-fit ${
                        user.is_active
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        <span className="w-2 h-2 rounded-full mr-2 ${user.is_active ? 'bg-green-400' : 'bg-red-400'}"></span>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {user.is_verified && (
                        <span className="inline-flex items-center px-2 py-1 text-sm font-medium rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30 w-fit">
                          ✓ Verified
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-5 w-40">
                    <div className="text-center">
                      <div className="text-lg font-bold text-yellow-400 mb-2">
                        {(user.total_shares || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-white space-y-0.5">
                        {(() => {
                          const purchased = user.purchased_shares || 0;
                          const bonus = user.bonus_shares || 0;
                          const converted = user.converted_shares || 0;
                          const commission = user.commission_shares || 0;

                          const parts = [];
                          if (purchased > 0) parts.push(`📈 ${purchased} purchased`);
                          if (bonus > 0) parts.push(`🎁 ${bonus} bonus`);
                          if (converted > 0) parts.push(`🔄 ${converted} converted`);
                          if (commission > 0) parts.push(`💰 ${commission} commission`);

                          if (parts.length === 0) {
                            return <div className="text-gray-500">No shares</div>;
                          }

                          return parts.map((part, idx) => (
                            <div key={idx} className="text-left">{part}</div>
                          ));
                        })()}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-5 w-32">
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-400">
                        ${(user.total_invested || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        💰 {user.approved_payments_count || 0} payments
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-5 w-32">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-400">
                        ${(user.total_commissions || 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        🤝 {user.commission_balances ? 'Active' : 'None'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-5 w-28">
                    <div className="text-center">
                      {(user.total_referrals || 0) > 0 ? (
                        <button
                          type="button"
                          onClick={() => openReferrals(user)}
                          title="View referrals"
                          className="text-lg font-bold text-purple-400 hover:text-purple-200 underline decoration-purple-400/40"
                        >
                          {(user.total_referrals || 0).toLocaleString()}
                        </button>
                      ) : (
                        <div className="text-lg font-bold text-gray-500">0</div>
                      )}
                      <div className="text-sm text-gray-500 mt-1">
                        {(user.total_referrals || 0) > 0
                          ? (expandedUsers.has(user.id) ? 'Hide referrals' : 'View referrals')
                          : (user.referred_by ? 'Referred' : 'Direct')}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-5 w-24">
                    <div className="text-center">
                      {user.kyc_information ? (
                        <span className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full border ${
                          user.kyc_information.kyc_status === 'completed'
                            ? 'bg-green-500/20 text-green-400 border-green-500/30'
                            : user.kyc_information.kyc_status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                            : 'bg-red-500/20 text-red-400 border-red-500/30'
                        }`}>
                          {user.kyc_information.kyc_status === 'completed' ? '✅' :
                           user.kyc_information.kyc_status === 'pending' ? '⏳' : '❌'}
                          <span className="ml-1 capitalize">{user.kyc_information.kyc_status}</span>
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500 bg-gray-700/30 px-3 py-1.5 rounded-full border border-gray-600/30">
                          🆔 None
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-5 w-32">
                    <div className="text-center">
                      {user.referred_by ? (
                        <div className="space-y-1">
                          <div className="text-sm font-medium text-blue-400">
                            {user.referred_by.referrer?.username || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.referred_by.referrer?.id || 'N/A'}
                          </div>
                          {user.referred_by.referrer?.full_name && (
                            <div className="text-sm text-white truncate max-w-[120px]">
                              {user.referred_by.referrer.full_name}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 bg-gray-700/30 px-3 py-1.5 rounded-full border border-gray-600/30">
                          🤝 None
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-5 text-sm text-gray-300 w-28">
                    <div className="text-center">
                      <div className="font-medium">
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {new Date(user.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-5 w-48">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleViewReferralLinks(user)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors"
                      >
                        🔗 Links
                      </button>
                      <button
                        onClick={() => handleLoginAsUser(user)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-orange-500/20 text-orange-400 border border-orange-500/30 hover:bg-orange-500/30 transition-colors"
                      >
                        👤 Login As
                      </button>
                      <button
                        onClick={() => handleViewFinancials(user)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30 transition-colors"
                      >
                        💰 Financials
                      </button>
                      <button
                        onClick={() => handleFinancialActions(user)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 transition-colors"
                      >
                        ⚙️ Manage
                      </button>
                      <button
                        onClick={() => handleCommunication(user)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 transition-colors"
                      >
                        📨 Message
                      </button>
                      <button
                        onClick={() => handleEditUser(user)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 hover:bg-blue-500/30 transition-colors"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                          user.is_active
                            ? 'bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30'
                            : 'bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30'
                        }`}
                      >
                        {user.is_active ? '🔴 Deactivate' : '🟢 Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600/20 text-red-400 border border-red-600/30 hover:bg-red-600/30 transition-colors"
                      >
                        🔒 Quarantine
                      </button>
                      <button
                        onClick={() => handleScrubDeleteUser(user.id)}
                        disabled={!(isOwner && isDeletable(user))}
                        title={
                          !isOwner
                            ? 'Only Johan, JP, or Ruvan can delete accounts'
                            : !isDeletable(user)
                              ? 'Only inactive or quarantined accounts can be deleted'
                              : 'Delete account & free email for reuse (financial data preserved)'
                        }
                        className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
                          isOwner && isDeletable(user)
                            ? 'bg-red-700/30 text-red-300 border-red-700/40 hover:bg-red-700/40'
                            : 'bg-gray-700/20 text-gray-500 border-gray-700/30 cursor-not-allowed opacity-50'
                        }`}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {renderDownlineRows(user.id, 1)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-gray-800/30 border-t border-gray-600 flex items-center justify-between rounded-b-lg">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-300 font-medium">
                📊 Showing <span className="text-yellow-400 font-bold">{((currentPage - 1) * usersPerPage) + 1}</span> to <span className="text-yellow-400 font-bold">{Math.min(currentPage * usersPerPage, totalUsers)}</span> of <span className="text-yellow-400 font-bold">{totalUsers}</span> users
              </div>
              <select
                value={usersPerPage}
                onChange={(e) => {
                  setUsersPerPage(Number(e.target.value))
                  setCurrentPage(1) // Reset to first page when changing page size
                }}
                className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded-lg border border-gray-600 hover:bg-gray-600"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center px-4 py-2 text-sm font-medium bg-gray-700 text-gray-300 rounded-lg border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 hover:border-gray-500 transition-colors"
              >
                ← Previous
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-white">Page</span>
                <span className="px-3 py-1 text-sm font-bold text-yellow-400 bg-gray-800 rounded-lg border border-gray-600">
                  {currentPage}
                </span>
                <span className="text-sm text-white">of</span>
                <span className="px-3 py-1 text-sm font-bold text-gray-300 bg-gray-800 rounded-lg border border-gray-600">
                  {totalPages}
                </span>
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-4 py-2 text-sm font-medium bg-gray-700 text-gray-300 rounded-lg border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 hover:border-gray-500 transition-colors"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Users Cards - Mobile/Tablet */}
      <div className={`md:hidden space-y-4 ${!loading && currentUsers.length === 0 && debouncedSearchTerm ? 'hidden' : ''}`}>
        {currentUsers.map((user) => (
          <div key={user.id} className="glass-card p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-white">
                  {user.full_name || user.username}
                </h3>
                <p className="text-sm text-white">@{user.username}</p>
                <p className="text-sm text-gray-500">ID: {user.id}</p>
              </div>
              <div className="flex flex-col space-y-1">
                <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full w-fit ${
                  user.is_active
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
                {user.is_admin && (
                  <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full bg-yellow-500/20 text-yellow-400 w-fit">
                    Admin
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <p className="text-sm text-white uppercase tracking-wider">Contact</p>
                <p className="text-sm text-gray-300">{user.email}</p>
                {user.phone && (
                  <p className="text-sm text-white">{user.phone}</p>
                )}
              </div>

              <div>
                <p className="text-sm text-white uppercase tracking-wider">Telegram</p>
                {user.telegram_users && user.telegram_users.length > 0 ? (
                  <div className="text-sm">
                    <p className="text-blue-400">@{user.telegram_users[0].username}</p>
                    <p className="text-white text-sm">
                      {user.telegram_users[0].first_name} {user.telegram_users[0].last_name}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Not connected</p>
                )}
              </div>
            </div>

            {/* Sponsor Information */}
            <div className="mb-4 p-3 bg-blue-900/20 rounded-lg border border-blue-500/20">
              <p className="text-sm text-blue-400 uppercase tracking-wider mb-2">🤝 Sponsor</p>
              {user.referred_by ? (
                <div>
                  <p className="text-sm font-medium text-blue-300">
                    {user.referred_by.referrer?.full_name || user.referred_by.referrer?.username || 'Unknown'}
                  </p>
                  <p className="text-sm text-white">
                    ID: {user.referred_by.referrer?.id || 'N/A'}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No sponsor (Direct signup)</p>
              )}
            </div>

            {/* Financial Information */}
            <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-800/30 rounded-lg">
              <div className="text-center">
                <p className="text-sm text-white uppercase tracking-wider">Shares</p>
                <p className="text-lg font-bold text-yellow-400">{(user.total_shares || 0).toLocaleString()}</p>
                <div className="text-sm text-gray-500 mt-1 space-y-0.5">
                  {(() => {
                    const purchased = user.purchased_shares || 0;
                    const bonus = user.bonus_shares || 0;
                    const converted = user.converted_shares || 0;
                    const commission = user.commission_shares || 0;

                    const parts = [];
                    if (purchased > 0) parts.push(`📈 ${purchased}`);
                    if (bonus > 0) parts.push(`🎁 ${bonus}`);
                    if (converted > 0) parts.push(`🔄 ${converted}`);
                    if (commission > 0) parts.push(`💰 ${commission}`);

                    if (parts.length === 0) {
                      return <div>No shares</div>;
                    }

                    return parts.map((part, idx) => (
                      <div key={idx}>{part}</div>
                    ));
                  })()}
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm text-white uppercase tracking-wider">Amount Paid</p>
                <p className="text-lg font-bold text-green-400">${(user.total_invested || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">{user.approved_payments_count || 0} payments</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-white uppercase tracking-wider">Commissions</p>
                <p className="text-lg font-bold text-blue-400">${(user.total_commissions || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">{user.commission_balances ? 'Active' : 'None'}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-white uppercase tracking-wider">Referrals</p>
                <p className="text-lg font-bold text-purple-400">{(user.total_referrals || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-500">{user.referred_by ? 'Referred' : 'Direct'}</p>
              </div>
            </div>

            {/* Referral Information */}
            {(user.referred_by || (user.total_referrals || 0) > 0) && (
              <div className="mb-4 p-3 bg-purple-900/20 rounded-lg border border-purple-500/20">
                <p className="text-sm text-purple-400 uppercase tracking-wider mb-2">Referral Network</p>
                {user.referred_by && (
                  <div className="mb-2">
                    <p className="text-sm text-white">Referred by:</p>
                    <p className="text-sm text-purple-300">
                      {user.referred_by.referrer?.full_name || user.referred_by.referrer?.username}
                    </p>
                  </div>
                )}
                {(user.total_referrals || 0) > 0 && user.referrals_made && user.referrals_made.length > 0 && (
                  <div>
                    <p className="text-sm text-white mb-2">Direct Referrals ({user.total_referrals}):</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {user.referrals_made.map((ref: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between text-sm bg-gray-800/50 px-2 py-1 rounded">
                          <span className="text-purple-300">
                            {ref.referred?.full_name || ref.referred?.username || `User #${ref.referred_id}`}
                          </span>
                          <span className="text-gray-500">
                            {ref.referred?.username && `@${ref.referred.username}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* KYC Information */}
            {user.kyc_information && (
              <div className="mb-4 p-3 bg-orange-900/20 rounded-lg border border-orange-500/20">
                <p className="text-sm text-orange-400 uppercase tracking-wider mb-2">KYC Status</p>
                <div className="flex justify-between items-center">
                  <div>
                    <span className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${
                      user.kyc_information.kyc_status === 'completed'
                        ? 'bg-green-500/20 text-green-400'
                        : user.kyc_information.kyc_status === 'pending'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {user.kyc_information.kyc_status}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-white">
                      {user.kyc_information.full_legal_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {user.kyc_information.country_name}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center pt-3 border-t border-gray-700">
              <div>
                <p className="text-sm text-white">
                  Created: {new Date(user.created_at).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-500">
                  Last Activity: {new Date(user.last_activity || user.created_at).toLocaleDateString()}
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-white mr-2">Activity:</span>
                  <div className="w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"
                      style={{ width: `${user.activity_score || 0}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-white ml-2">{user.activity_score || 0}%</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => alert('TEST BUTTON WORKS!')}
                  className="bg-red-500 text-white px-6 py-3 rounded text-lg font-bold border-4 border-yellow-400 shadow-lg"
                  style={{ minWidth: '120px', minHeight: '50px' }}
                >
                  🔴 BIG TEST 🔴
                </button>
                <button
                  onClick={() => handleViewFinancials(user)}
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
                >
                  💰 Financials
                </button>
                <button
                  onClick={() => handleViewReferralLinks(user)}
                  className="text-cyan-400 hover:text-cyan-300 text-sm font-medium"
                >
                  🔗 Links
                </button>
                <button
                  onClick={() => handleLoginAsUser(user)}
                  className="text-orange-400 hover:text-orange-300 text-sm font-medium"
                >
                  👤 Login As
                </button>
                <button
                  onClick={() => handleFinancialActions(user)}
                  className="text-green-400 hover:text-green-300 text-sm font-medium"
                >
                  ⚙️ Manage
                </button>
                <button
                  onClick={() => window.open(`#financial-audit?userId=${user.id}`, '_blank')}
                  className="text-yellow-400 hover:text-yellow-300 text-sm font-medium"
                  title="Generate Financial Audit Report"
                >
                  📊 Audit
                </button>
                <button
                  onClick={() => handleCommunication(user)}
                  className="text-purple-400 hover:text-purple-300 text-sm font-medium"
                >
                  📨 Message
                </button>
                <button
                  onClick={() => handleEditUser(user)}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => toggleUserStatus(user.id, user.is_active)}
                  className={`text-sm font-medium ${
                    user.is_active
                      ? 'text-red-400 hover:text-red-300'
                      : 'text-green-400 hover:text-green-300'
                  }`}
                >
                  {user.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDeleteUser(user.id)}
                  className="text-red-400 hover:text-red-300 text-sm font-medium"
                >
                  Quarantine
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Mobile Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4">
            <div className="text-sm text-white">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedUser(null)
          }}
          onSave={() => {
            loadUsers()
            setShowEditModal(false)
            setSelectedUser(null)
          }}
          adminUser={adminUser}
        />
      )}

      {/* Financial Modal */}
      {showFinancialModal && selectedUser && (
        <UserFinancialModal
          user={selectedUser}
          isOpen={showFinancialModal}
          onClose={() => {
            setShowFinancialModal(false)
            setSelectedUser(null)
          }}
        />
      )}

      {/* Financial Actions Modal */}
      {showFinancialActionsModal && selectedUser && (
        <FinancialActionsModal
          user={selectedUser}
          isOpen={showFinancialActionsModal}
          onClose={() => {
            setShowFinancialActionsModal(false)
            setSelectedUser(null)
          }}
          adminUser={adminUser}
          onUpdate={() => {
            loadUsers()
          }}
        />
      )}

      {/* Communication Modal */}
      {showCommunicationModal && selectedUser && (
        <CommunicationModal
          user={selectedUser}
          isOpen={showCommunicationModal}
          onClose={() => {
            setShowCommunicationModal(false)
            setSelectedUser(null)
          }}
          adminUser={adminUser}
          onUpdate={() => {
            loadUsers()
          }}
        />
      )}

      {/* Bulk Actions Modal */}
      {showBulkActionsModal && (
        <BulkActionsModal
          isOpen={showBulkActionsModal}
          onClose={() => {
            setShowBulkActionsModal(false)
          }}
          selectedUsers={selectedUsers}
          adminUser={adminUser}
          onUpdate={() => {
            loadUsers()
          }}
          onClearSelection={clearSelection}
        />
      )}

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onUserAdded={() => {
          loadUsers()
          setShowAddUserModal(false)
        }}
        adminUser={adminUser}
      />

      {/* Referral Links Modal */}
      {selectedUser && (
        <ReferralLinksModal
          user={selectedUser}
          isOpen={showReferralLinksModal}
          onClose={() => {
            setShowReferralLinksModal(false)
            setSelectedUser(null)
          }}
        />
      )}

      {/* User Impersonation Modal */}
      {selectedUser && (
        <UserImpersonationModal
          user={selectedUser}
          isOpen={showImpersonationModal}
          onClose={() => {
            setShowImpersonationModal(false)
            setSelectedUser(null)
          }}
          adminUser={adminUser}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </div>
  )
}

// Export with admin authentication protection
export const UserManager = withAdminAuth(UserManagerComponent, 'canManageUsers')
